import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFuturasTareasStore } from "../store/futurasTareas";
import { AutoDisablingButton } from "../elementos/AutoDisablingButton";
import axios from "axios";
import toast from "react-hot-toast";
import { globalApiError } from "../utils/generalApiError";
import { ApiErrorResponse } from "../DTO/ApiResponseError";
import { FuturaTarea, Prioridad } from "../DTO/FuturaTarea";
import LoadingModal from "../elementos/LoadingModal";

interface FormularioTareaProps {
  id: string | null;
  tarea: FuturaTarea | null;
}

const FormularioTarea: React.FC<FormularioTareaProps> = ({ id, tarea }) => {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState<Prioridad>("SIN_PRIORIDAD");
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { createTarea } = useFuturasTareasStore();

  useEffect(() => {
    if (tarea) {
      setNombre(tarea?.nombre);
      setDescripcion(tarea.descripcion);
      setPrioridad(tarea.prioridad ?? "SIN_PRIORIDAD");
    }
  }, [tarea]);

  useEffect(() => {
    setDisabled(false);
  }, []);

  const defaultObject = () => {
    setNombre("");
    setDescripcion("");
    setFieldErrors({});
  };

  const validateForm = () => {
    return nombre === "" || descripcion === "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setFieldErrors({});
    e.preventDefault();
    try {
      if (!validateForm()) {
        if (id === null) {
          setDisabled(true);
          setLoading(true);
          await createTarea(null, nombre, descripcion, prioridad);
          toast.success("Se ha creado la tarea correctamente");
        } else {
          setDisabled(true);
          setLoading(true);
          await createTarea(id, nombre, descripcion, prioridad);
          toast.success("Se ha editado la tarea correctamente");
        }
        navigate("/futuraTarea");
        defaultObject();
      } else {
        toast.error("Debes completar todo el formulario antes de continuar");
      }
    } catch (error) {
      if (axios.isAxiosError<ApiErrorResponse>(error)) {
        const apiError = error.response?.data;
        const errors = apiError?.fieldErrors ?? {};

        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          return;
        }
      }

      toast.error(globalApiError(error));
    } finally {
      setDisabled(false);
      setLoading(false);
    }
  };
  return (
    <div className="container mt-4">
      <LoadingModal show={loading} message={id === null ? "Creando tarea futura..." : "Guardando tarea futura..."} />
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <input type="hidden" value={tarea?.id} />
          <label htmlFor="nombre" className="form-label">
            Nombre de la tarea
          </label>
          <input
            type="text"
            className="form-control"
            id="nombre"
            placeholder="Ej. Hacer la compra"
            required
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
          {fieldErrors?.nombre && <div className="alert alert-danger py-2">{fieldErrors?.nombre}</div>}
        </div>

        <div className="mb-3 textarea-con-contador">
          <label htmlFor="descripcion" className="form-label">
            Descripción
          </label>
          <textarea
            className="form-control"
            id="descripcion"
            rows={3}
            maxLength={255}
            placeholder="Detalles adicionales de la tarea"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
          <span className="contador-descripcion" aria-live="polite">
            {descripcion.length}/255
          </span>
        </div>
        {fieldErrors?.descripcion && (
          <div className="alert alert-danger py-2">
            {fieldErrors.descripcion}
          </div>
        )}

        <div className="col-md-3" style={{ marginBottom: "25px" }}>
          <label htmlFor="completadaSelect" className="form-label">
            Prioridad:
          </label>
          <select
            id="completadaSelect"
            className="form-select"
            value={prioridad}
            onChange={(e) => {
              setPrioridad(e.target.value as Prioridad);
            }}
          >
            <option value="SIN_PRIORIDAD">Sin prioridad</option>
            <option value="ALTA">Alta</option>
            <option value="MEDIA">Media</option>
            <option value="BAJA">Baja</option>
          </select>
          {fieldErrors.prioridad && (<div className="alert alert-danger py-2"> {fieldErrors.prioridad} </div>)}
        </div>

        <AutoDisablingButton
          disabled={disabled}
          type="submit"
          className="btn btn-success me-2"
        >
          Guardar Tarea
        </AutoDisablingButton>

        <Link to={"/futuratarea"} className="btn btn-primary">
          {" "}
          Volver atras
        </Link>
      </form>
    </div>
  );
};

export default FormularioTarea;
